import React, { useState } from 'react'
import { ClientNationalityDto } from '@/api/Clients/Types/ClientDetailedDto'
import { NameDto } from '@/api/Common/Types'
import {
  FormBaseFieldBlock,
  FormBaseFieldGroup,
  FormBaseFieldLabel,
  FormBaseRow,
  FormBaseSectionBlock,
} from '@/components/Form/Form.styles'
import { Select } from '@/components/Select'
import { Button } from '@/components/ui/button'
import { Heading3, Heading5 } from '@/components/ui/typography'
import { Select as AntSelect } from 'antd'
import { Box } from 'components/Box'
import { Modal } from 'components/CustomModal'
import { SectionContainer } from 'components/QuestionAnswerSection'
import { type AMLField } from 'components/QuestionAnswerSection/AmlFieldAnswerSection'
import { useAppSelector } from 'store'

const { Option } = AntSelect

interface ClientTestAMLFieldsProps {
  amlFields: AMLField[]
  countryOfResidence?: NameDto<string> | null
  isPoliticallyExposedPerson: boolean
  nationalities: ClientNationalityDto[]
  onCountryChange?: (countryId: string) => void
  onPEPChange: (isPEP: boolean) => void
  onNationalitiesChange: (nationalities: ClientNationalityDto[]) => void
  onAMLSubmit?: () => void
}

interface ModalData {
  type: 'nationality'
  data: ClientNationalityDto
  editId?: string
}

export const ClientTestAMLFields: React.FC<ClientTestAMLFieldsProps> = ({
  amlFields,
  countryOfResidence,
  isPoliticallyExposedPerson,
  nationalities,
  onCountryChange,
  onPEPChange,
  onNationalitiesChange,
  onAMLSubmit,
}) => {
  const countries = useAppSelector((state) => state.identity.countries)
  const [isModalData, setModalData] = useState<ModalData | undefined>(undefined)

  const handleSaveNationality = (modalData: ModalData, newNationality: ClientNationalityDto) => {
    let updated: ClientNationalityDto[]
    if (modalData.editId) {
      updated = nationalities.map((n) => (n.id === modalData.editId ? newNationality : n))
    } else {
      updated = [...nationalities, newNationality]
    }

    if (newNationality.isPrimary) {
      updated = updated.map((n) => ({
        ...n,
        isPrimary: n.id === newNationality.id,
      }))
    }

    if (updated.length > 0 && !updated.some((n) => n.isPrimary)) {
      updated[0].isPrimary = true
    }

    onNationalitiesChange(updated)
    setModalData(undefined)
  }

  const handleDeleteNationality = (nationalityId: string) => {
    if (nationalities.length <= 1) return

    const filtered = nationalities.filter((n) => n.id !== nationalityId)
    if (!filtered.some((n) => n.isPrimary)) {
      filtered[0].isPrimary = true
    }
    onNationalitiesChange(filtered)
  }

  const rowStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '220px 1fr',
    alignItems: 'center',
    columnGap: '16px',
    width: '100%',
  }

  const controlWrapStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    minHeight: 36,
    width: '100%',
  }

  const checkboxStyle: React.CSSProperties = {
    width: 16,
    height: 16,
    margin: 0,
  }

  const actionsStyle: React.CSSProperties = {
    display: 'flex',
    gap: '8px',
    justifyContent: 'flex-end',
    alignItems: 'center',
  }

  return (
    <>
      {isModalData && (
        <Modal visible={!!isModalData} onClose={() => setModalData(undefined)}>
          <Box
            display='flex'
            flexDirection='column'
            style={{ padding: '16px', gap: 12, width: 520 }}
          >
            <Heading3>{isModalData?.editId ? 'Edit' : 'Add'} Nationality</Heading3>

            <FormBaseFieldBlock>
              <FormBaseRow style={rowStyle as any}>
                <FormBaseFieldLabel>Country</FormBaseFieldLabel>
                <FormBaseFieldGroup style={controlWrapStyle as any}>
                  <AntSelect
                    placeholder='Select nationality'
                    value={isModalData.data.id || undefined}
                    onChange={(value: string) => {
                      const country = countries.find((x) => x.id === value)
                      if (country) {
                        setModalData({
                          ...isModalData,
                          data: { ...isModalData.data, id: country.id, name: country.name },
                        })
                      }
                    }}
                    showSearch
                    allowClear
                    style={{ width: '100%' }}
                  >
                    {countries
                      .filter(
                        (g) =>
                          !nationalities.map((x) => x.id).includes(g.id) ||
                          g.id === isModalData.data.id
                      )
                      .map((country) => (
                        <Option key={country.id} value={country.id}>
                          {country.name}
                        </Option>
                      ))}
                  </AntSelect>
                </FormBaseFieldGroup>
              </FormBaseRow>
            </FormBaseFieldBlock>

            <FormBaseFieldBlock>
              <FormBaseRow style={rowStyle as any}>
                <FormBaseFieldLabel>Primary</FormBaseFieldLabel>
                <FormBaseFieldGroup style={controlWrapStyle as any}>
                  <input
                    type='checkbox'
                    checked={isModalData.data.isPrimary}
                    style={checkboxStyle}
                    onChange={(e) =>
                      setModalData({
                        ...isModalData,
                        data: { ...isModalData.data, isPrimary: e.target.checked },
                      })
                    }
                  />
                </FormBaseFieldGroup>
              </FormBaseRow>
            </FormBaseFieldBlock>

            <Box
              display='flex'
              flexDirection='row'
              style={{ marginTop: 4, gap: 8, justifyContent: 'flex-end' }}
            >
              <Button
                type='button'
                onClick={() => {
                  if (isModalData.data.id) handleSaveNationality(isModalData, isModalData.data)
                }}
              >
                Save
              </Button>
              <Button type='button' onClick={() => setModalData(undefined)}>
                Cancel
              </Button>
            </Box>
          </Box>
        </Modal>
      )}

      <SectionContainer isNewWindow>
        <Box display='flex' flexDirection='column' style={{ width: '100%', gap: 16 }}>
          <Box display='flex' alignItems='center' justifyContent='space-between'>
            <Heading5>Personal Details</Heading5>
          </Box>

          <FormBaseSectionBlock>
            <FormBaseFieldLabel>AML Fields</FormBaseFieldLabel>

            {amlFields
              .filter((field) => field.reference === 'non_face_to_face')
              .map((field) => {
                const selectedId = field.currentValue?.id
                const selectedLabel =
                  field.currentValue?.amlFieldScore?.displayName ??
                  field.currentValue?.value ??
                  'Default'

                return (
                  <FormBaseFieldBlock key={field.id}>
                    <FormBaseRow style={rowStyle as any}>
                      <FormBaseFieldLabel>{field.displayName}</FormBaseFieldLabel>
                      <FormBaseFieldGroup style={controlWrapStyle as any}>
                        <Select value={selectedId} fullWidth disabled>
                          {selectedId ? (
                            <Option value={selectedId} key={selectedId}>
                              {selectedLabel}
                            </Option>
                          ) : (
                            <Option value='default' key='default'>
                              {selectedLabel}
                            </Option>
                          )}
                        </Select>
                      </FormBaseFieldGroup>
                    </FormBaseRow>
                  </FormBaseFieldBlock>
                )
              })}
          </FormBaseSectionBlock>

          <FormBaseSectionBlock>
            <FormBaseFieldLabel>Client AML Fields</FormBaseFieldLabel>

            <FormBaseFieldBlock>
              <FormBaseRow style={rowStyle as any}>
                <FormBaseFieldLabel>Country of Residence</FormBaseFieldLabel>
                <FormBaseFieldGroup style={controlWrapStyle as any}>
                  <Select
                    placeholder='Country of Residence'
                    value={countryOfResidence?.id || undefined}
                    allowClear
                    showSearch
                    fullWidth
                    onChange={(value: string) => onCountryChange?.(value)}
                  >
                    {countries.map((country) => (
                      <Option value={country.id} key={country.id}>
                        {country.name}
                      </Option>
                    ))}
                  </Select>
                </FormBaseFieldGroup>
              </FormBaseRow>
            </FormBaseFieldBlock>

            <FormBaseFieldBlock>
              <FormBaseRow style={rowStyle as any}>
                <FormBaseFieldLabel>Politically Exposed Person</FormBaseFieldLabel>
                <FormBaseFieldGroup style={controlWrapStyle as any}>
                  <input
                    type='checkbox'
                    checked={isPoliticallyExposedPerson}
                    style={checkboxStyle}
                    onChange={(e) => onPEPChange(e.target.checked)}
                  />
                </FormBaseFieldGroup>
              </FormBaseRow>
            </FormBaseFieldBlock>
          </FormBaseSectionBlock>

          <FormBaseSectionBlock>
            <Box
              display='flex'
              alignItems='center'
              justifyContent='space-between'
              style={{ gap: 12, marginBottom: 8 }}
            >
              <FormBaseFieldLabel style={{ margin: 0 }}>Nationalities</FormBaseFieldLabel>
              <Button
                type='button'
                onClick={() =>
                  setModalData({
                    type: 'nationality',
                    data: { name: '', isPrimary: false, id: '' },
                  })
                }
              >
                Add Nationality
              </Button>
            </Box>
            <Box
              display='flex'
              flexDirection='column'
              style={{
                width: '100%',
                border: '1px solid rgba(0,0,0,0.08)',
                borderRadius: 8,
                overflow: 'hidden',
              }}
            >
              <Box
                display='flex'
                alignItems='center'
                style={{
                  padding: '10px 12px',
                  fontWeight: 600,
                  background: 'rgba(0,0,0,0.03)',
                }}
              >
                <div style={{ flex: 1 }}>Nationality</div>
                <div style={{ width: 90, textAlign: 'center' }}>Primary</div>
                <div style={{ width: 180, textAlign: 'right' }}>Actions</div>
              </Box>

              {nationalities.length === 0 ? (
                <Box style={{ padding: '12px', opacity: 0.7 }}>No nationalities added.</Box>
              ) : (
                nationalities.map((nationality) => (
                  <Box
                    key={nationality.id}
                    display='flex'
                    alignItems='center'
                    style={{
                      padding: '10px 12px',
                      borderTop: '1px solid rgba(0,0,0,0.06)',
                    }}
                  >
                    <div style={{ flex: 1 }}>{nationality.name}</div>

                    <div style={{ width: 90, display: 'flex', justifyContent: 'center' }}>
                      <input
                        type='checkbox'
                        checked={nationality.isPrimary}
                        style={checkboxStyle}
                        onChange={(e) => {
                          if (!e.target.checked) return
                          const updated = nationalities.map((n) => ({
                            ...n,
                            isPrimary: n.id === nationality.id,
                          }))
                          onNationalitiesChange(updated)
                        }}
                      />
                    </div>

                    <div style={{ width: 180 }}>
                      <div style={actionsStyle}>
                        <button
                          type='button'
                          onClick={() =>
                            setModalData({
                              type: 'nationality',
                              data: nationality,
                              editId: nationality.id,
                            })
                          }
                          style={{ height: 32, padding: '0 10px' }}
                        >
                          Edit
                        </button>
                        <button
                          type='button'
                          onClick={() => handleDeleteNationality(nationality.id)}
                          style={{
                            height: 32,
                            padding: '0 10px',
                            cursor: nationalities.length === 1 ? 'not-allowed' : 'pointer',
                            opacity: nationalities.length === 1 ? 0.5 : 1,
                          }}
                          disabled={nationalities.length === 1}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </Box>
                ))
              )}
            </Box>
          </FormBaseSectionBlock>

          {onAMLSubmit && (
            <Box display='flex' justifyContent='flex-start' style={{ marginTop: 16 }}>
              <Button
                type='button'
                onClick={onAMLSubmit}
                style={{
                  justifyContent: 'center',
                  color: '#1b2337',
                  cursor: 'pointer',
                  opacity: 0.76,
                }}
              >
                Submit AML Information
              </Button>
            </Box>
          )}
        </Box>
      </SectionContainer>
    </>
  )
}
